import { useState } from 'react';
import type { OrgUnit, OrgUnitSelection } from '../../../types/orgUnit';
import { OrgUnitEmptyState } from './OrgUnitEmptyState';

interface Props {
  orgUnits: OrgUnit[];
  selection: OrgUnitSelection;
  onSelect: (sel: OrgUnitSelection) => void;
  /**
   * Create a new region. If parentId is supplied, the region is nested under
   * an organization-typed parent; otherwise it is created at root (legacy /
   * backwards-compat path).
   */
  onAddRegion: (parentId?: string) => void;
  onAddDistrict: (parentId: string) => void;
  /**
   * Create a new organization at the root (#310). Optional so older callers
   * that pre-date 3-level vocabulary continue to compile; pages should pass
   * this through to enable the "+ Add organization" affordance.
   */
  onAddOrganization?: () => void;
}

function isSelected(sel: OrgUnitSelection, id: string): boolean {
  return sel.kind === 'unit' && sel.id === id;
}

export function OrgUnitsTree({
  orgUnits,
  selection,
  onSelect,
  onAddRegion,
  onAddDistrict,
  onAddOrganization,
}: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // 3-level vocabulary (#310). Organizations are roots; regions nest under
  // organizations (or remain at root for backwards-compat with pre-#310 data);
  // districts nest under regions.
  const organizations = orgUnits.filter(
    (u) => u.type === 'organization' && !u.parent_id && !u.deleted_at,
  );
  const orgIds = new Set(organizations.map((o) => o.org_unit_id));

  const regionsByOrg = new Map<string, OrgUnit[]>();
  const orphanedRegions: OrgUnit[] = [];
  for (const u of orgUnits) {
    if (u.type !== 'region' || u.deleted_at) continue;
    if (u.parent_id && orgIds.has(u.parent_id)) {
      const arr = regionsByOrg.get(u.parent_id) ?? [];
      arr.push(u);
      regionsByOrg.set(u.parent_id, arr);
    } else if (!u.parent_id) {
      orphanedRegions.push(u);
    }
    // regions with a parent_id pointing to something that isn't an active
    // organization are dropped from rendering — they shouldn't exist in
    // practice, but if they do we surface them once their parent recovers.
  }

  const districtsByParent = new Map<string, OrgUnit[]>();
  for (const u of orgUnits) {
    if (u.type !== 'district' || u.deleted_at || !u.parent_id) continue;
    const arr = districtsByParent.get(u.parent_id) ?? [];
    arr.push(u);
    districtsByParent.set(u.parent_id, arr);
  }

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (orgUnits.length === 0) {
    return (
      <div className="bg-ems-bg-card border-r border-ems-border p-4">
        <OrgUnitEmptyState
          onCreateRegion={() => onAddRegion()}
          onCreateOrganization={onAddOrganization}
        />
      </div>
    );
  }

  const renderRegion = (region: OrgUnit, level: 1 | 2) => {
    const isCollapsed = collapsed.has(region.org_unit_id);
    const regionSelected = isSelected(selection, region.org_unit_id);
    const children = districtsByParent.get(region.org_unit_id) ?? [];
    const indent = level === 1 ? 'pl-2' : 'pl-6';
    const childIndent = level === 1 ? 'pl-8' : 'pl-12';

    return (
      <div key={region.org_unit_id}>
        <button
          type="button"
          role="treeitem"
          aria-level={level}
          aria-selected={regionSelected}
          aria-expanded={!isCollapsed}
          onClick={() => onSelect({ kind: 'unit', id: region.org_unit_id })}
          className={`w-full text-left flex justify-between items-center ${indent} pr-2 py-1.5 rounded ${
            regionSelected ? 'bg-ems-green/15 text-ems-text-primary' : ''
          }`}
        >
          <span className="flex items-center gap-1">
            <span
              role="button"
              tabIndex={0}
              aria-label={`${isCollapsed ? 'expand' : 'collapse'} ${region.name}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapse(region.org_unit_id);
              }}
              className="inline-block w-4 text-center"
            >
              {isCollapsed ? '▸' : '▾'}
            </span>
            <span>{region.name}</span>
          </span>
          <span className="text-ems-text-muted">
            {children.length} district{children.length === 1 ? '' : 's'}
          </span>
        </button>

        {!isCollapsed &&
          children.map((d) => {
            const dSelected = isSelected(selection, d.org_unit_id);
            return (
              <button
                key={d.org_unit_id}
                type="button"
                role="treeitem"
                aria-level={level === 1 ? 2 : 3}
                aria-selected={dSelected}
                onClick={() => onSelect({ kind: 'unit', id: d.org_unit_id })}
                className={`w-full text-left ${childIndent} pr-2 py-1 rounded ${
                  dSelected ? 'bg-ems-green/15 text-ems-text-primary' : ''
                }`}
              >
                • {d.name}
              </button>
            );
          })}

        {regionSelected && (
          <div className={`${childIndent} pb-2`}>
            <button
              type="button"
              onClick={() => onAddDistrict(region.org_unit_id)}
              className="text-xs text-ems-green hover:text-ems-green-bright"
            >
              + Add district
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="bg-ems-bg-card border-r border-ems-border p-2 text-xs text-ems-text-secondary overflow-y-auto"
      role="tree"
      aria-label="Org units"
    >
      <div className="flex justify-between items-center mb-2 px-2 gap-2">
        <div className="font-semibold text-ems-text-primary">Org Units</div>
        <div className="flex gap-1">
          {onAddOrganization && (
            <button
              type="button"
              onClick={onAddOrganization}
              className="text-xs px-2 py-1 rounded bg-ems-forest text-ems-green border border-ems-green/20 hover:bg-ems-forest/80"
            >
              + Add organization
            </button>
          )}
          <button
            type="button"
            onClick={() => onAddRegion()}
            className="text-xs px-2 py-1 rounded bg-ems-forest text-ems-green border border-ems-green/20 hover:bg-ems-forest/80"
          >
            + Add region
          </button>
        </div>
      </div>

      {organizations.map((org) => {
        const isCollapsed = collapsed.has(org.org_unit_id);
        const orgSelected = isSelected(selection, org.org_unit_id);
        const regions = regionsByOrg.get(org.org_unit_id) ?? [];
        return (
          <div key={org.org_unit_id}>
            <button
              type="button"
              role="treeitem"
              aria-level={1}
              aria-selected={orgSelected}
              aria-expanded={!isCollapsed}
              onClick={() => onSelect({ kind: 'unit', id: org.org_unit_id })}
              className={`w-full text-left flex justify-between items-center p-1.5 rounded font-medium ${
                orgSelected ? 'bg-ems-green/15 text-ems-text-primary' : 'text-ems-text-primary'
              }`}
            >
              <span className="flex items-center gap-1">
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`${isCollapsed ? 'expand' : 'collapse'} ${org.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapse(org.org_unit_id);
                  }}
                  className="inline-block w-4 text-center"
                >
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <span>{org.name}</span>
              </span>
              <span className="text-ems-text-muted">
                {regions.length} region{regions.length === 1 ? '' : 's'}
              </span>
            </button>

            {!isCollapsed && regions.map((r) => renderRegion(r, 2))}

            {orgSelected && (
              <div className="pl-8 pb-2">
                <button
                  type="button"
                  onClick={() => onAddRegion(org.org_unit_id)}
                  className="text-xs text-ems-green hover:text-ems-green-bright"
                >
                  + Add region
                </button>
              </div>
            )}
          </div>
        );
      })}

      {orphanedRegions.map((r) => renderRegion(r, 1))}
    </div>
  );
}
